package co.maytes.standupbot.dashboard.features.onboarding

import androidx.compose.runtime.Composable
import androidx.compose.runtime.ReadOnlyComposable
import androidx.compose.runtime.compositionLocalOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import co.maytes.standupbot.shared.dto.billing.PlanLimitNudgeDto
import co.maytes.standupbot.shared.dto.standup.RosterChangeResultDto
import kotlinx.browser.window
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.builtins.serializer
import kotlinx.serialization.json.Json
import org.w3c.dom.Storage

enum class OnboardingStep(val path: String, val label: String) {
    Slack("/app/onboarding/slack", "Slack"),
    Linear("/app/onboarding/linear", "Linear"),
    Setup("/app/onboarding/setup", "Setup"),
    Preview("/app/onboarding/preview", "Preview"),
    ;

    companion object {
        val ordered = values().toList()
        fun fromPath(path: String): OnboardingStep? = values().firstOrNull { path.startsWith(it.path) }

        fun resumePath(slackConnected: Boolean, linearConnected: Boolean): String = when {
            !slackConnected -> Slack.path
            !linearConnected -> Linear.path
            else -> Setup.path
        }
    }
}

class OnboardingStore {
    var selectedChannelId: String? by mutableStateOf(null)
        private set

    var selectedChannelName: String? by mutableStateOf(null)
        private set

    var standupNameOverride: String? by mutableStateOf(null)
        private set

    var scheduleDays: List<String> by mutableStateOf(DEFAULT_DAYS)
        private set

    var scheduleHour: Int by mutableStateOf(DEFAULT_HOUR)
        private set

    var scheduleMinute: Int by mutableStateOf(DEFAULT_MINUTE)
        private set

    var scheduleTimezone: String by mutableStateOf(DEFAULT_TIMEZONE)
        private set

    var createdStandupId: String? by mutableStateOf(null)
        private set

    var createdStandupName: String? by mutableStateOf(null)
        private set

    var createdRoster: RosterChangeResultDto? by mutableStateOf(null)
        private set

    var createdNudge: PlanLimitNudgeDto? by mutableStateOf(null)
        private set

    var previewRunId: String? by mutableStateOf(null)
        private set

    val standupName: String
        get() = standupNameOverride ?: selectedChannelName.orEmpty()

    init {
        restore()
    }

    fun setChannel(id: String, name: String?) {
        selectedChannelId = id
        selectedChannelName = name
        persist()
    }

    fun setStandupName(name: String) {
        standupNameOverride = name
        persist()
    }

    fun setSchedule(days: List<String>, hour: Int, minute: Int, timezone: String) {
        scheduleDays = days
        scheduleHour = hour
        scheduleMinute = minute
        scheduleTimezone = timezone
        persist()
    }

    fun setScheduleTimezone(timezone: String) {
        scheduleTimezone = timezone
        persist()
    }

    fun setCreatedStandup(id: String?, name: String?, roster: RosterChangeResultDto?, nudge: PlanLimitNudgeDto?) {
        createdStandupId = id
        createdStandupName = name
        createdRoster = roster
        createdNudge = nudge
        previewRunId = null
        persist()
    }

    fun setPreviewRunId(id: String?) {
        previewRunId = id
        persist()
    }

    private fun persist() {
        val s = session() ?: return
        selectedChannelId?.let { s.setItem(KEY_CHANNEL_ID, it) } ?: s.removeItem(KEY_CHANNEL_ID)
        selectedChannelName?.let { s.setItem(KEY_CHANNEL_NAME, it) } ?: s.removeItem(KEY_CHANNEL_NAME)
        standupNameOverride?.let { s.setItem(KEY_NAME, it) } ?: s.removeItem(KEY_NAME)
        s.setItem(KEY_DAYS, Json.encodeToString(ListSerializer(String.serializer()), scheduleDays))
        s.setItem(KEY_HOUR, scheduleHour.toString())
        s.setItem(KEY_MINUTE, scheduleMinute.toString())
        s.setItem(KEY_TZ, scheduleTimezone)
        createdStandupId?.let { s.setItem(KEY_CREATED_ID, it) } ?: s.removeItem(KEY_CREATED_ID)
        createdStandupName?.let { s.setItem(KEY_CREATED_NAME, it) } ?: s.removeItem(KEY_CREATED_NAME)
        createdRoster
            ?.let { s.setItem(KEY_CREATED_ROSTER, Json.encodeToString(RosterChangeResultDto.serializer(), it)) }
            ?: s.removeItem(KEY_CREATED_ROSTER)
        createdNudge
            ?.let { s.setItem(KEY_NUDGE, Json.encodeToString(PlanLimitNudgeDto.serializer(), it)) }
            ?: s.removeItem(KEY_NUDGE)
        previewRunId?.let { s.setItem(KEY_RUN_ID, it) } ?: s.removeItem(KEY_RUN_ID)
    }

    private fun restore() {
        val s = session() ?: return
        selectedChannelId = s.getItem(KEY_CHANNEL_ID)
        selectedChannelName = s.getItem(KEY_CHANNEL_NAME)
        standupNameOverride = s.getItem(KEY_NAME)
        s.getItem(KEY_DAYS)?.let { raw ->
            runCatching {
                Json.decodeFromString(ListSerializer(String.serializer()), raw)
            }.onSuccess { scheduleDays = it }
        }
        s.getItem(KEY_HOUR)?.toIntOrNull()?.let { scheduleHour = it }
        s.getItem(KEY_MINUTE)?.toIntOrNull()?.let { scheduleMinute = it }
        s.getItem(KEY_TZ)?.let { scheduleTimezone = it }
        createdStandupId = s.getItem(KEY_CREATED_ID)
        createdStandupName = s.getItem(KEY_CREATED_NAME)
        s.getItem(KEY_CREATED_ROSTER)?.let { raw ->
            runCatching {
                Json.decodeFromString(RosterChangeResultDto.serializer(), raw)
            }.onSuccess { createdRoster = it }
        }
        s.getItem(KEY_NUDGE)?.let { raw ->
            runCatching {
                Json.decodeFromString(PlanLimitNudgeDto.serializer(), raw)
            }.onSuccess { createdNudge = it }
        }
        previewRunId = s.getItem(KEY_RUN_ID)
    }

    private fun session(): Storage? = runCatching { window.sessionStorage }.getOrNull()

    companion object {
        private const val KEY_CHANNEL_ID = "onboarding.channelId"
        private const val KEY_CHANNEL_NAME = "onboarding.channelName"
        private const val KEY_NAME = "onboarding.standupName"
        private const val KEY_DAYS = "onboarding.scheduleDays"
        private const val KEY_HOUR = "onboarding.scheduleHour"
        private const val KEY_MINUTE = "onboarding.scheduleMinute"
        private const val KEY_TZ = "onboarding.scheduleTz"
        private const val KEY_CREATED_ID = "onboarding.createdStandupId"
        private const val KEY_CREATED_NAME = "onboarding.createdStandupName"
        private const val KEY_CREATED_ROSTER = "onboarding.createdRoster"
        private const val KEY_NUDGE = "onboarding.createdNudge"
        private const val KEY_RUN_ID = "onboarding.previewRunId"

        private val DEFAULT_DAYS = listOf("MON", "TUE", "WED", "THU", "FRI")
        private const val DEFAULT_HOUR = 9
        private const val DEFAULT_MINUTE = 30
        private const val DEFAULT_TIMEZONE = "UTC"
    }
}

val LocalOnboardingStore = compositionLocalOf<OnboardingStore> {
    error("OnboardingStore not provided — wrap your tree in AppEntry")
}

@Composable
@ReadOnlyComposable
fun rememberOnboardingStore(): OnboardingStore = LocalOnboardingStore.current
